/* eslint-disable no-shadow */
/* eslint-disable max-lines-per-function */
/* eslint-disable complexity */

import React, {useContext, useMemo} from 'react';
import {Box, Col, Row} from 'components/Grid';
import {useIsSkeleton} from 'modules/skeleton/hooks/useIsSkeleton';
import {useAppSelector} from 'core/redux/hooks/useAppSelector';
import {selectProductData} from 'modules/product-card/selectors';
import style from './style.module.scss';
import {ProductBuyMobilePanel} from 'modules/product-card/components/ProductBuyMobilePanel';
import {useSetInitialCounterCount} from 'hooks/product/useSetInitialCounterCount';
import {ProductHeader} from 'modules/products/components/ProductHeader';
import {scroller} from 'react-scroll';
import {SPECIFICATIONS_SCROLL_NAME} from 'modules/product-card/constants';
import {useIsProductFineAndRare} from 'modules/product-card/hooks/useIsProductFineAndRare';
import {getAdditionalDataCatalog} from 'modules/products/helpers';
import {ProductTopDesktopPanelContainer} from 'modules/products/components/ProductTopDesktopPanelContainer';
import {HeaderStickyPortal} from 'components/Header/BasicHeader/components/HeaderStickyPortal';
import {useProductImagesAltText} from 'modules/product-card/hooks/useProductImagesAltText';
import {ProductRecommendationsType} from 'modules/recommendations/types';
import {useIsProductAvailableForUser} from 'modules/products/hooks/useIsProductAvailableForUser';
import {ProductSchema} from 'modules/product-card/components/Marking';
import ProductGallery from 'modules/product-card/pages/ProductPage/ProductGallery';
import ProductInfoBlock from 'modules/product-card/pages/ProductPage/ProductInfoBlock';
import ProductBuyBlock from 'modules/product-card/pages/ProductPage/ProductBuyBlock';
import ProductMobileBlock from 'modules/product-card/pages/ProductPage/ProductMobileBlock';
import ProductExpertReviewBlock from 'modules/product-card/pages/ProductPage/ProductExpertReviewBlock';
import ProductTagsBlock from 'modules/product-card/pages/ProductPage/ProductTagsBlock';
import ProductDetailedSpecificationsBlock from 'modules/product-card/pages/ProductPage/ProductDetailedSpecificationsBlock';
import ProductModalsBlock from 'modules/product-card/pages/ProductPage/ProductModalsBlock';
import ProductVintagesBlock from 'modules/product-card/pages/ProductPage/ProductVintagesBlock';
import ProductSeoBlock from 'modules/product-card/pages/ProductPage/ProductSeoBlock';
import {ProductVintage} from 'modules/products/components/ProductVintage';
import FlocktoryItemView from 'components/flocktory/FlocktoryItemView';
import {Breadcrumbs} from 'modules/breadcrumbs/components/Breadcrumbs';
import useProductPageRecommendationsState from 'modules/product-card/hooks/useProductPageRecommendationsState';
import {ProductPageContext} from 'modules/product-card/context';
import {NEXT_PUBLIC_MASTER_HOST} from 'core/helpers/env';
import dynamic from 'next/dynamic';
import {Recommendations} from 'components/Recommendations';

const ProductDetailedReviews = dynamic(
    () =>
        import('modules/products/components/ProductDetailedReviews').then((m) => ({
            default: m.ProductDetailedReviews,
        })),
    {ssr: false, loading: () => null},
);

const ProductArticlesBlock = dynamic(
    () =>
        import('modules/product-card/pages/ProductPage/ProductArticles/ProductArticlesBlock').then((m) => ({
            default: m.default,
        })),
    {ssr: false, loading: () => null},
);

const {WebOtherItemsOfTheBrandPdp, SimilarInFlavor, Personal, WebPdpPurchasedTogether, RecentlyViewed, Popular} =
    ProductRecommendationsType;

// eslint-disable-next-line complexity
export const ProductPage = () => {
    const productData = useAppSelector(selectProductData);
    const {seo} = useContext(ProductPageContext);
    const isSkeleton = useIsSkeleton();

    const isFineAndRareCategory = Boolean(useIsProductFineAndRare());

    const {
        id,
        bubble,
        canBuy,
        canReserve,
        catalogRusName,
        code,
        labels,
        media,
        properties,
        title,
        vintages,
        similar,
        yandexSplitInformer,
    } = productData || {};

    const {recommendationsData} = useProductPageRecommendationsState();

    const onlineOnlyLabel = labels?.find((label) => 'ONLINE_ONLY' === label.code);
    const additionalDataCatalog = getAdditionalDataCatalog(catalogRusName, properties);

    const isProductAvailable = useIsProductAvailableForUser(canBuy, canReserve);

    const basePrice = productData?.price?.base?.price;
    const discountPrice = productData?.price?.discount?.price;
    const seoPrice = isProductAvailable ? (discountPrice ?? basePrice ?? 0) : (basePrice ?? 0);

    const sliderImageAltText = useProductImagesAltText();

    useSetInitialCounterCount();

    const productImages = useMemo(() => {
        const {imagePreview, images} = media || {};
        // eslint-disable-next-line no-nested-ternary
        return imagePreview ? [imagePreview, ...(images || [])] : images ? [...images] : [];
    }, [media]);

    const breadcrumbsList = seo?.breadcrumbs || [];

    const isShowNotifyAvailability = useMemo(
        () => Boolean(productData) && !isProductAvailable,
        [isProductAvailable, productData],
    );

    const handleSeeAllInfo = () => {
        scroller.scrollTo(SPECIFICATIONS_SCROLL_NAME, {
            offset: -200,
            smooth: true,
        });
    };

    const shouldShowVintages = Array.isArray(vintages) && 0 < vintages.length;

    const shouldShowTags = useMemo(() => {
        const groups = productData?.tagGroups ?? [];
        return groups.some((group) => 0 < group.tags.length);
    }, [productData]);

    const giftBanner = useMemo(() => {
        const selectionBanner = productData?.selectionBanner;
        const giftBanner = productData?.giftBanner;

        const isShowBanner = Boolean(selectionBanner) || Boolean(giftBanner);

        return {
            isShowBanner,
            title: selectionBanner?.shortDescription ?? giftBanner?.title ?? '',
            url: selectionBanner?.sectionPageUrl ?? giftBanner?.url ?? '',
            imageUrl: selectionBanner?.giftImg ?? (null !== giftBanner ? null : null),
        };
    }, [productData]);

    const growingDiscountBanner = useMemo(() => {
        return productData?.labels?.find((label) => 'GROWING_DISCOUNT' === label.code);
    }, [productData]);

    return (
        <>
            <ProductSchema url={`${NEXT_PUBLIC_MASTER_HOST}/catalog/product/${productData?.code}`} />
            <div className={style.main}>
                <Box className={style.breadCrumbs} commonPaddingOnMobileTablet={true}>
                    <Breadcrumbs breadcrumbs={breadcrumbsList} />
                </Box>
                <Box commonPaddingOnMobileTablet={true}>
                    <ProductHeader
                        id={id}
                        catalogRusName={additionalDataCatalog}
                        labels={labels}
                        title={title}
                        canBuy={canBuy}
                        canReserve={canReserve}
                        similar={similar}
                    />
                    <Row className={style.contentRow}>
                        <Col>
                            <div className={style.content}>
                                <div className={style.areaGallery}>
                                    <ProductGallery
                                        bubble={bubble}
                                        videoUrl={media?.video}
                                        videoImage={media?.videoPreview}
                                        images={productImages}
                                        imagesAltText={sliderImageAltText}
                                    />
                                </div>
                                <div className={style.areaMain}>
                                    <ProductInfoBlock
                                        isFineAndRareCategory={isFineAndRareCategory}
                                        handleSeeAllInfo={handleSeeAllInfo}
                                    />
                                    <ProductBuyBlock
                                        similar={similar}
                                        isOnlineOnly={Boolean(onlineOnlyLabel)}
                                        isShowNotifyAvailability={isShowNotifyAvailability}
                                        giftBanner={giftBanner}
                                        growingDiscountBanner={growingDiscountBanner}
                                    />
                                    {/* Блок винтажей десктоп/планшет */}
                                    {shouldShowVintages && <ProductVintagesBlock vintages={vintages} code={code} />}
                                </div>
                            </div>
                            <ProductMobileBlock
                                similar={similar}
                                isOnlineOnly={Boolean(onlineOnlyLabel)}
                                isShowNotifyAvailability={isShowNotifyAvailability}
                                isFineAndRareCategory={isFineAndRareCategory}
                                shouldShowVintages={shouldShowVintages}
                                vintages={vintages || []}
                                code={code || ''}
                                giftBanner={giftBanner}
                                id={id}
                                yandexSplitInformer={yandexSplitInformer}
                                growingDiscountBanner={growingDiscountBanner}
                            />
                        </Col>
                    </Row>
                    <div className={style.vintageTablet}>
                        <ProductVintage items={vintages} parentCode={code} />
                    </div>
                </Box>

                {/* Блоки рекомендаций - похожие товары или персональное */}
                {/* логика такая, что если БЕ не отдаёт похожие товары - то он должен отдавать персональные предложения и подставляются они, иначе похожие */}
                {(() => {
                    const hasPersonal = 0 < (recommendationsData[Personal]?.items ?? []).length;
                    const hasSimilarFlavor = 0 < (recommendationsData[SimilarInFlavor]?.items ?? []).length;
                    // eslint-disable-next-line no-nested-ternary
                    const data = hasPersonal
                        ? recommendationsData[Personal]
                        : hasSimilarFlavor
                          ? recommendationsData[SimilarInFlavor]
                          : recommendationsData[Popular];
                    const hasData = 0 < (data?.items ?? []).length;

                    return (
                        hasData && (
                            <Recommendations
                                data={data}
                                analytics={{
                                    listName: data?.title,
                                }}
                            />
                        )
                    );
                })()}

                {/* блок экспертного мнения */}
                {productData?.expertReview && !isSkeleton && (
                    <ProductExpertReviewBlock expertReview={productData?.expertReview} />
                )}

                {/* Блок характеристик товара */}
                <ProductDetailedSpecificationsBlock />

                {/* Блоки рекомендаций - Другие товары этого бренда */}
                {0 < (recommendationsData[WebOtherItemsOfTheBrandPdp]?.items ?? []).length && (
                    <Recommendations
                        data={recommendationsData[WebOtherItemsOfTheBrandPdp]}
                        analytics={{
                            listName: recommendationsData[WebOtherItemsOfTheBrandPdp]?.title,
                        }}
                    />
                )}

                {!isFineAndRareCategory && productData && <ProductDetailedReviews product={productData} />}

                {0 < (recommendationsData[WebPdpPurchasedTogether]?.items ?? []).length && (
                    <Recommendations
                        data={recommendationsData[WebPdpPurchasedTogether]}
                        analytics={{
                            listName: recommendationsData[WebPdpPurchasedTogether]?.title,
                        }}
                    />
                )}

                {/* Блок статей */}
                {0 < (productData?.articles ?? []).length && (
                    <ProductArticlesBlock
                        articleIds={productData?.articles ?? []}
                        isFineAndRareCategory={isFineAndRareCategory}
                        isSkeleton={isSkeleton}
                    />
                )}

                {/* Блок "Вы недавно смотрели" */}
                {0 < (recommendationsData[RecentlyViewed]?.items ?? []).length && (
                    <Recommendations
                        data={recommendationsData[RecentlyViewed]}
                        analytics={{
                            listName: recommendationsData[RecentlyViewed]?.title,
                        }}
                    />
                )}

                <ProductBuyMobilePanel />
            </div>

            {/* Блок тегов */}
            {!isFineAndRareCategory && shouldShowTags && <ProductTagsBlock tagGroups={productData?.tagGroups} />}

            {/* SEO-блок с описанием и TrustSlider */}
            <ProductSeoBlock
                isFineAndRareCategory={isFineAndRareCategory}
                isSkeleton={isSkeleton}
                productData={productData}
                seoPrice={seoPrice}
            />

            <HeaderStickyPortal>
                <ProductTopDesktopPanelContainer />
            </HeaderStickyPortal>

            {/* Модальные окна */}
            <ProductModalsBlock />

            <FlocktoryItemView />
        </>
    );
};
